import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import H2 from 'UIComponents/elements/headings/H2';
import { productLineToHub } from 'self-service-api/constants/ProductLineToHub';
import * as ProductLines from 'self-service-api/constants/ProductLines'; // @ts-expect-error dependency missing types

import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import * as AdminModalTypes from 'ui-addon-upgrades/_core/common/constants/AdminModalTypes';
import { track } from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
import { requestAdditionalSeats } from '../../upgrade/requestAdditionalSeats';
import { requestQuotePurchase } from '../../upgrade/requestQuotePurchase';
import { requestSKU } from '../../upgrade/requestSKU';
import { requestSeatUpgrade } from '../../upgrade/requestSeatUpgrade';
import { requestTrial } from '../../upgrade/requestTrial';
import { getProductNameTextFromApiNameOrDefault, getProductNameTextOrDefault } from '../adapters/getProductNameText';
import { RequestSeatModalWrapper } from './seatsModals/RequestSeatModalWrapper';
export const REQUEST_FUNCTION_BY_TYPE = {
  [AdminModalTypes.REQUEST_ADDITIONAL_SEATS]: requestAdditionalSeats,
  [AdminModalTypes.REQUEST_SEAT_UPGRADE]: requestSeatUpgrade,
  [AdminModalTypes.REQUEST_SEAT_UPGRADE_BANNER]: requestSeatUpgrade,
  [AdminModalTypes.REQUEST_SKU]: requestSKU,
  [AdminModalTypes.REQUEST_TRIAL]: requestTrial,
  [AdminModalTypes.REQUEST_QUOTE]: requestQuotePurchase
};

const AdminModal = ({
  modalType,
  upgradeData,
  productLine,
  onClose,
  canViewNewSeats,
  showSeatSelector,
  canViewNewSeatRequestModal
}) => {
  const getUpgradeSource = ({
    app,
    screen,
    uniqueId
  }) => `${app}-${screen}-admin-modal-${uniqueId}`;

  const [requestSeatText, setRequestSeatText] = useState('');
  const [requestedSeat, setRequestedSeat] = useState('');
  const apiNameToUse = canViewNewSeats && upgradeData.apiName ? upgradeData.apiName : UpgradeProductToApiNameMap[upgradeData.upgradeProduct];
  useEffect(() => {
    track('viewUpgradeAdminPrompt', Object.assign({
      apiName: apiNameToUse,
      modalType,
      upgradeSource: upgradeData.source || getUpgradeSource(upgradeData)
    }, getUpgradeDataTrackingProperties(upgradeData), {
      trialId: upgradeData.trialId
    })); // keep empty dependency array so that this event only fires once, not on every rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleError = () => {
    FloatingAlertStore.addAlert({
      message: I18n.text('upgrades.adminModal.alertNotification.error.message'),
      titleText: I18n.text('upgrades.adminModal.alertNotification.error.titleText'),
      type: 'danger'
    });
  };

  const handleSuccess = () => {
    FloatingAlertStore.addAlert({
      message: I18n.text('upgrades.adminModal.alertNotification.success.message'),
      titleText: I18n.text('upgrades.adminModal.alertNotification.success.titleText'),
      type: 'success'
    });
  };

  const getRequestArgs = () => {
    const BASE_ARGS = {
      apiName: apiNameToUse
    };

    switch (modalType) {
      case AdminModalTypes.REQUEST_SEAT_UPGRADE:
        if (canViewNewSeatRequestModal) {
          return Object.assign({}, BASE_ARGS, {
            requestSeatText,
            requestedSeat
          });
        } else {
          return BASE_ARGS;
        }

      case AdminModalTypes.REQUEST_QUOTE:
        if (productLine) return {
          hub: productLineToHub[productLine]
        };
        break;

      default:
        return BASE_ARGS;
    }

    return BASE_ARGS;
  };

  const handleClick = () => {
    const hasRequestFunction = Object.keys(REQUEST_FUNCTION_BY_TYPE).includes(modalType);

    if (hasRequestFunction) {
      const args = getRequestArgs();

      if (REQUEST_FUNCTION_BY_TYPE[modalType]) {
        REQUEST_FUNCTION_BY_TYPE[modalType](args).then(handleSuccess).catch(handleError);
      }
    }

    onClose();
  };

  const productNameToUse = () => {
    return canViewNewSeats && upgradeData.apiName ? getProductNameTextFromApiNameOrDefault(upgradeData.apiName) : getProductNameTextOrDefault(upgradeData.upgradeProduct);
  };

  if (modalType === AdminModalTypes.REQUEST_SEAT_UPGRADE && canViewNewSeatRequestModal) {
    return /*#__PURE__*/_jsx(RequestSeatModalWrapper, {
      modalType: modalType,
      handleConfirm: handleClick,
      onClose: onClose,
      showSeatSelector: showSeatSelector,
      canViewNewSeats: canViewNewSeats,
      requestSeatText: requestSeatText,
      setRequestSeatText: setRequestSeatText,
      setRequestedSeat: setRequestedSeat
    });
  }

  return /*#__PURE__*/_jsxs(UIModal, {
    size: "auto",
    width: 540,
    use: "success",
    "data-selenium": "admin-only-modal",
    "data-test-id": `admin-only-modal-${modalType}`,
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: () => onClose()
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          "data-unit-test": "admin-modal__title",
          "data-test-id": "admin-modal__title",
          message: `upgrades.adminModal.${modalType}.title`
        })
      })]
    }), /*#__PURE__*/_jsx(UIDialogBody, {
      children: /*#__PURE__*/_jsx("p", {
        className: "p-right-4",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          "data-unit-test": "admin-modal__paragraph",
          "data-test-id": "admin-modal__paragraph",
          message: `upgrades.adminModal.${modalType}.paragraph`,
          options: {
            productName: productNameToUse()
          }
        })
      })
    }), /*#__PURE__*/_jsx(UIDialogFooter, {
      children: /*#__PURE__*/_jsx("div", {
        "data-selenium": "admin-modal-close-button",
        children: /*#__PURE__*/_jsx(UIButton, {
          className: "m-left-0",
          use: "primary",
          onClick: handleClick,
          "data-test-id": "admin-modal-submit-button",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            "data-unit-test": "admin-modal__button",
            "data-test-id": "admin-modal__button",
            message: `upgrades.adminModal.${modalType}.button`
          })
        })
      })
    })]
  });
};

AdminModal.propTypes = {
  apiName: PropTypes.string,
  modalType: PropTypes.oneOf(Object.values(AdminModalTypes || {})),
  onClose: PropTypes.func.isRequired,
  productLine: PropTypes.oneOf(Object.values(ProductLines)),
  upgradeData: PropTypes.object.isRequired
};
export default AdminModal;